import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, KanbanSquare, List, Plus, Search, Upload, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, cleanParams, downloadFile, formatErr } from "@/lib/api";
import { LEAD_TYPES, SOURCES, STAGES, TEMPS, TEMP_STYLE } from "@/lib/constants";
import { useAssignableOptions, useUserMap } from "@/hooks/useData";
import { Chip, EmptyState, NSelect, PageHeader, Spinner } from "@/components/common";
import { LeadCard } from "@/components/leads/LeadCard";
import LeadKanban from "@/components/leads/LeadKanban";
import LeadImportDialog from "@/components/leads/LeadImportDialog";
import { cn } from "@/lib/utils";

const KEYS = ["temperature", "stage", "source", "assigned_to", "lead_type", "followup", "q"];

export default function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const view = params.get("view") || "list";
  const [q, setQ] = useState(filters.q);
  const [importOpen, setImportOpen] = useState(false);
  const userMap = useUserMap();
  const assignOpts = useAssignableOptions();

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== filters.q) set("q", q);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const apiFilters = cleanParams(view === "board" ? { ...filters, stage: "" } : filters);
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", apiFilters],
    queryFn: async () => (await api.get("/leads", { params: apiFilters })).data,
  });

  const exportXlsx = async () => {
    try {
      await downloadFile("/leads/export", apiFilters, "leads.xlsx");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const hasFilters = KEYS.some((k) => filters[k]);
  return (
    <>
      <PageHeader
        title="Leads"
        testid="leads-title"
        subtitle={`${leads.length} ${leads.length === 1 ? "lead" : "leads"}`}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="leads-import-button"><Upload className="mr-1.5 h-4 w-4" />Import</Button>
            <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="leads-export-button"><Download className="mr-1.5 h-4 w-4" />Export</Button>
            <Button size="sm" asChild data-testid="leads-add-button"><Link to="/leads/new"><Plus className="mr-1.5 h-4 w-4" />Add lead</Link></Button>
          </>
        }
      />
      <div className="mb-4 flex items-center gap-2 overflow-x-auto scrollbar-none">
        <Chip testid="leads-tab-all" active={!filters.followup} onClick={() => set("followup", "")}>All</Chip>
        <Chip testid="leads-tab-today" active={filters.followup === "today"} onClick={() => set("followup", "today")}>Today's follow-ups</Chip>
        <Chip testid="leads-tab-overdue" active={filters.followup === "overdue"} onClick={() => set("followup", "overdue")} className={filters.followup === "overdue" ? "!bg-destructive !border-destructive" : ""}>Overdue</Chip>
        <span className="mx-1 h-5 w-px bg-border" />
        {TEMPS.map((t) => (
          <button
            key={t}
            data-testid={`lead-temperature-${t.toLowerCase()}-filter`}
            onClick={() => set("temperature", filters.temperature === t ? "" : t)}
            className={cn("h-8 shrink-0 rounded-full border px-3 text-xs font-bold transition-opacity", TEMP_STYLE[t], filters.temperature && filters.temperature !== t && "opacity-40")}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex shrink-0 rounded-full border bg-card p-0.5">
          <button data-testid="leads-view-list" onClick={() => set("view", "")} className={cn("flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold", view === "list" && "bg-primary text-primary-foreground")}><List className="h-3.5 w-3.5" />List</button>
          <button data-testid="leads-view-board" onClick={() => set("view", "board")} className={cn("flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold", view === "board" && "bg-primary text-primary-foreground")}><KanbanSquare className="h-3.5 w-3.5" />Board</button>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
        <div className="relative col-span-2 md:col-span-3 lg:col-span-2">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <input data-testid="leads-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, location…" className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        {view !== "board" && <NSelect testid="leads-stage-filter" value={filters.stage} onChange={(v) => set("stage", v)} placeholder="All stages" options={[{ value: "active", label: "All active" }, ...STAGES]} />}
        <NSelect testid="leads-source-filter" value={filters.source} onChange={(v) => set("source", v)} placeholder="All sources" options={SOURCES} />
        <NSelect testid="leads-type-filter" value={filters.lead_type} onChange={(v) => set("lead_type", v)} placeholder="All lead types" options={LEAD_TYPES} />
        <NSelect testid="leads-assigned-filter" value={filters.assigned_to} onChange={(v) => set("assigned_to", v)} placeholder="Anyone" options={[{ value: "unassigned", label: "Unassigned" }, ...assignOpts]} />
      </div>
      {hasFilters && (
        <button data-testid="leads-clear-filters" onClick={() => { setQ(""); setParams(view === "board" ? { view } : {}, { replace: true }); }} className="-mt-3 mb-4 flex items-center gap-1 text-xs font-semibold text-primary">
          <X className="h-3.5 w-3.5" />Clear filters
        </button>
      )}
      {isLoading ? (
        <Spinner />
      ) : !leads.length ? (
        <EmptyState icon={Users} testid="leads-empty" title="No leads found" text={hasFilters ? "Try clearing some filters." : "Add your first lead or import from Excel."} />
      ) : view === "board" ? (
        <LeadKanban leads={leads} userMap={userMap} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="leads-list">
          {leads.map((l) => <LeadCard key={l.id} lead={l} owner={userMap[l.assigned_to]} />)}
        </div>
      )}
      <LeadImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}
